import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ArrowUpRight, X } from "lucide-react";
import { http, goUrl } from "@/lib/data";
import { useBrokers, useInsurers, usePlatforms, pct, tone, shortCat } from "@/lib/format";
import { VERTICALS, rank } from "@/lib/searchIndex";

const useDebounced = (v, ms = 200) => {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
};

const SEG = { crypto: ["Crypto Exchange", "crypto exchange", "Crypto exchange · fees & coins"], commodities: ["Commodity Broker", "commodity broker mcx", "MCX commodity trading · gold, silver, crude"], forex: ["Forex Broker", "forex currency broker", "Currency trading · USD/INR & more"] };
const INS = { life: "Life Insurer", health: "Health Insurer" };

const useSources = () => {
  const { data: b } = useBrokers();
  const { data: ins } = useInsurers();
  const { data: cr } = usePlatforms("crypto");
  const { data: cm } = usePlatforms("commodities");
  const { data: fx } = usePlatforms("forex");
  const { data: re } = useQuery({ queryKey: ["re-cities"], queryFn: () => http.get("/re/cities").then((r) => r.data) });
  const { data: projects } = useQuery({ queryKey: ["re-projects-all"], queryFn: () => http.get("/re/projects").then((r) => r.data.items) });
  return useMemo(() => ({
    providers: [
      ...(b?.items || []).map((x) => ({ key: x.id, title: x.name, tag: "Stock Broker", sub: "Stocks, demat & trading account", text: `${x.name} stock broker demat ${x.type || ""}`, to: `/stock-brokers?focus=${x.id}`, go: x.id })),
      ...(ins?.items || []).map((x) => ({ key: x.id, title: x.name, tag: INS[x.type] || "Insurer", text: `${x.name} insurer insurance ${x.type}`, to: `/insurance?type=${x.type}&focus=${x.id}`, go: x.id })),
      ...[cr, cm, fx].flatMap((d) => d?.items || []).map((x) => ({ key: x.id, title: x.name, tag: SEG[x.segment][0], sub: SEG[x.segment][2], text: `${x.name} ${SEG[x.segment][1]}`, to: `/markets?tab=${x.segment}&focus=${x.id}`, go: x.id })),
    ],
    cities: (re?.items || []).map((x) => ({ key: x.id, title: `Real estate in ${x.name}`, tag: "City", sub: `₹${x.avg_price_sqft.toLocaleString("en-IN")}/sq.ft avg · ${x.state}`, text: `${x.name} ${x.state}`, to: `/real-estate?city=${x.id}` })),
    localities: (re?.items || []).flatMap((x) => x.localities.map((l) => ({ key: `${x.id}-${l.name}`, title: `${l.name}, ${x.name}`, tag: "Locality", sub: `₹${l.price_sqft.toLocaleString("en-IN")}/sq.ft · ${l.rental_yield}% yield`, text: `${l.name} ${x.name}`, to: `/real-estate?city=${x.id}` }))),
    projects: (projects || []).map((p) => ({ key: p.id, title: p.name, tag: "Project", sub: `${p.developer} · ${p.locality}, ${p.city}`, text: `${p.name} ${p.developer} ${p.locality} ${p.city} ${p.type}`, to: `/real-estate/project/${p.id}` })),
  }), [b, ins, cr, cm, fx, re, projects]);
};

const buildGroups = (q, src, funds) => {
  if (q.length < 2) return [];
  const sections = rank(q, VERTICALS, (v) => `${v.title} ${v.words}`, 4).map((v) => ({ ...v, tag: "Section" }));
  const reIntent = sections.some((v) => v.key === "realestate");
  const places = [...rank(q, src.cities, (x) => x.text, 3), ...rank(q, src.localities, (x) => x.text, 3)];
  const providers = rank(q, src.providers, (x) => x.text, 6);
  const named = providers.some((x) => x.title.toLowerCase().startsWith(q.toLowerCase()));
  const groups = [
    ...(named ? [["Providers", providers], ["Sections", sections]] : [["Sections", sections], ["Providers", providers]]),
    ["Real estate", [...(places.length || !reIntent ? places : src.cities.slice(0, 4)), ...rank(q, src.projects, (x) => x.text, 3)]],
    ["Mutual funds", (funds || []).slice(0, sections.length ? 4 : 6).map((x) => ({ key: x.code, title: x.name, tag: "Mutual Fund", sub: shortCat(x.category), to: `/mutual-funds/${x.code}`, fund: x }))],
  ];
  return groups.filter(([, items]) => items.length);
};

const useResults = (q) => {
  const src = useSources();
  const { data: funds } = useQuery({ queryKey: ["search", q], queryFn: () => http.get("/mf/funds", { params: { q, limit: 6, sort: "aum_cr" } }).then((r) => r.data.items), enabled: q.length >= 2 });
  const groups = useMemo(() => buildGroups(q, src, funds), [q, src, funds]);
  return { groups, pick: (raw) => buildGroups(raw, src, null).flatMap(([, items]) => items)[0] };
};

const Row = ({ x, active, onHover }) => {
  const tid = x.key.replace(/[^a-z0-9-]/gi, "").toLowerCase();
  return (
    <div onMouseEnter={onHover} className={`flex items-center gap-2 pr-3 border-b border-slate-100 transition-colors ${active ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
      <Link to={x.to} data-testid={`search-result-${tid}`} className="flex-1 min-w-0 flex items-center justify-between gap-3 pl-5 py-2.5">
        <span className="min-w-0"><span className="block font-semibold text-slate-900 truncate">{x.title}</span>{x.sub && <span className="block text-xs text-slate-500 truncate">{x.sub}</span>}</span>
        {x.fund ? <span className={`font-mono text-sm shrink-0 ${tone(x.fund.r3y)}`}>3Y {pct(x.fund.r3y)}</span> : <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500 shrink-0">{x.tag}</span>}
      </Link>
      {x.go && <a href={goUrl(x.go, "search")} target="_blank" rel="noopener noreferrer" data-testid={`search-visit-${tid}`} className="shrink-0 h-8 px-2.5 rounded-lg border border-slate-200 hover:border-[#10B981] text-xs font-bold text-[#059669] inline-flex items-center gap-1">Visit<ArrowUpRight className="w-3.5 h-3.5" /></a>}
    </div>
  );
};

const Results = ({ groups, active, setActive, q }) => {
  let i = -1;
  if (!groups.length) return <p data-testid="search-no-results" className="px-5 py-6 text-sm text-slate-500">No results for “{q}”. Try a broker, fund, city or a topic like “crypto” or “term insurance”.</p>;
  return groups.map(([label, items]) => (
    <div key={label} data-testid={`search-group-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="px-5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-[#059669]">{label}</p>
      {items.map((x) => { i += 1; const n = i; return <Row key={`${label}-${x.key}`} x={x} active={active === n} onHover={() => setActive(n)} />; })}
    </div>
  ));
};

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim());
  const nav = useNavigate();
  const box = useRef(null);
  const { groups, pick } = useResults(dq);
  const flat = groups.flatMap(([, items]) => items);

  useEffect(() => {
    const h = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => setActive(0), [dq]);

  const go = (to) => { setOpen(false); nav(to); };
  const submit = (e) => {
    e.preventDefault();
    const now = q.trim();
    if (now.length < 2) return;
    const hit = now === dq ? flat[active] : pick(now);
    go(hit?.to || `/mutual-funds?q=${encodeURIComponent(now)}`);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") setOpen(true);
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Escape") setOpen(false);
  };
  const show = open && dq.length >= 2;

  return (
    <div ref={box} className="relative w-full max-w-2xl">
      <form onSubmit={submit} className="relative" role="search">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input data-testid="global-search-input" value={q} onFocus={() => setOpen(true)} onKeyDown={onKey} onChange={(e) => { setQ(e.target.value); setOpen(true); }} autoComplete="off"
          placeholder="Search brokers, funds, insurance, crypto, cities…" className="w-full h-14 sm:h-16 rounded-2xl bg-white text-[#0F172A] pl-14 pr-40 text-base outline-none ring-4 ring-white/10 focus:ring-[#10B981]/50 shadow-2xl" />
        {q && <button type="button" data-testid="global-search-clear" onClick={() => { setQ(""); setOpen(false); }} aria-label="Clear search" className="absolute right-28 sm:right-32 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
        <button data-testid="global-search-submit" className="absolute right-2 top-1/2 -translate-y-1/2 h-10 sm:h-12 px-5 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold text-sm transition-colors">Search</button>
      </form>
      {show && (
        <div data-testid="global-search-results" onClick={() => setOpen(false)} className="absolute z-40 mt-2 w-full max-h-[70vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200 text-left">
          <Results groups={groups} active={active} setActive={setActive} q={dq} />
          <Link to={`/mutual-funds?q=${encodeURIComponent(dq)}`} data-testid="search-see-all-funds" className="flex items-center gap-1 px-5 py-3 text-sm font-bold text-[#059669] hover:bg-emerald-50">Search all mutual funds for “{dq}” <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      )}
    </div>
  );
};
